import { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, TimePicker, DatePicker, InputNumber } from 'antd';
import { X, ChevronDown, Clock, Calendar } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { TimekeepingStatus } from '../../types/timekeeping';
import { MODAL_STATUS_OPTIONS } from '@/features/timekeeping/constants';
import { systemSettingsService, type WorkShift } from '@/services/system-settings.service';
import styles from './TimekeepingModal.module.css';

const { TextArea } = Input;
const { Option } = Select;

export interface TimekeepingFormData {
  status: TimekeepingStatus;
  employeeCode: string;
  employeeName: string;
  date: Dayjs;
  checkIn: Dayjs | null;
  checkOut: Dayjs | null;
  shift: string;
  coefficient: number;
  overtimeHours: number;
  note?: string;
}

export interface TimekeepingModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TimekeepingFormData) => Promise<void>;
  initialData?: {
    id: string;
    dateValue: string;
    employeeName: string;
    employeeCode: string;
    shift: string;
    checkIn: string;
    checkOut: string;
    coefficient: number;
    overtimeHours: number;
    statuses: readonly TimekeepingStatus[];
    note?: string;
  };
}

const parseTime = (time: string | null | undefined): Dayjs | null => {
  if (!time || time === '--:--' || time === '00:00') return null;
  return dayjs(time, 'HH:mm');
};

const calculateCoefficient = (checkIn: Dayjs | null, checkOut: Dayjs | null): number => {
  if (!checkIn || !checkOut) return 0;
  
  const minutes = checkOut.diff(checkIn, 'minute', true);
  const standardMinutes = 8 * 60; // 480 phút = 8 giờ
  
  if (minutes >= standardMinutes) {
    return 1.0;
  }
  
  const coefficient = minutes / standardMinutes;
  return Math.round(coefficient * 1000) / 1000;
};

export const TimekeepingModal: React.FC<TimekeepingModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [workShifts, setWorkShifts] = useState<WorkShift[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(false);

  // Load work shifts when modal opens
  useEffect(() => {
    if (open) {
      const loadWorkShifts = async () => {
        try {
          setLoadingShifts(true);
          const shifts = await systemSettingsService.getWorkShifts();
          // Only show active work shifts
          const activeShifts = shifts.filter((shift) => shift.enabled);
          setWorkShifts(activeShifts);
        } catch (error) {
          console.error('Failed to load work shifts:', error);
          setWorkShifts([]);
        } finally {
          setLoadingShifts(false);
        }
      };
      loadWorkShifts();
    }
  }, [open]);

  useEffect(() => {
    if (open && initialData) {
      const checkIn = parseTime(initialData.checkIn);
      const checkOut = parseTime(initialData.checkOut);
      const calculatedCoefficient = calculateCoefficient(checkIn, checkOut);
      
      // Trích xuất status từ note nếu có, nếu không thì lấy từ statuses
      const actualStatus = initialData.statuses[0] || 'Đủ công';
      let initialStatus: TimekeepingStatus = actualStatus;
      let noteWithoutMetadata = initialData.note || '';
      
      // Đọc overtimeHours từ note (format: OVERTIME:3)
      let overtimeHoursFromNote = initialData.overtimeHours || 0;
      if (initialData.note) {
        const overtimeMatch = initialData.note.match(/OVERTIME:([^\n]+)/);
        if (overtimeMatch) {
          const overtimeValue = parseFloat(overtimeMatch[1].trim());
          if (!isNaN(overtimeValue)) {
            overtimeHoursFromNote = Math.max(0, Math.round(overtimeValue * 100) / 100);
          }
        }
      }
      
      if (actualStatus !== 'Vắng có lương' && actualStatus !== 'Vắng không lương' && actualStatus !== 'Vắng mặt') {
        // Giữ nguyên trạng thái để hiển thị trong dropdown (sẽ disabled)
        initialStatus = actualStatus;
      } else if (initialData.note) {
        // Kiểm tra trong note xem có status đặc biệt không
        const statusMatch = initialData.note.match(/STATUS:([^\n]+)/);
        if (statusMatch) {
          const savedStatus = statusMatch[1].trim();
          if (savedStatus === 'Vắng có lương' || savedStatus === 'Vắng không lương') {
            initialStatus = savedStatus as TimekeepingStatus;
          }
        }
      }
      
      // Loại bỏ STATUS: và OVERTIME: khỏi note để hiển thị (chỉ hiển thị nội dung ghi chú thực tế)
      if (noteWithoutMetadata) {
        noteWithoutMetadata = noteWithoutMetadata.replace(/STATUS:[^\n]+\n?/g, '').replace(/OVERTIME:[^\n]+\n?/g, '').trim();
      }
      
      form.setFieldsValue({
        status: initialStatus,
        employeeCode: initialData.employeeCode,
        employeeName: initialData.employeeName,
        date: dayjs(initialData.dateValue, 'YYYY-MM-DD'),
        checkIn,
        checkOut,
        shift: initialData.shift,
        coefficient: calculatedCoefficient,
        overtimeHours: overtimeHoursFromNote,
        note: noteWithoutMetadata,
      });
    } else if (open) {
      form.resetFields();
    }
  }, [open, initialData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(values);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closeIcon={<X size={20} />}
      className={styles.modal}
      width={750}
      destroyOnHidden
      style={{ top: 40 }}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Cập nhật chấm công</h2>
          <p className={styles.modalSubtitle}>Thay đổi thông tin chấm công</p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className={styles.form}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Trạng thái"
                rules={[
                  { required: true, message: 'Vui lòng chọn trạng thái' },
                ]}
              >
                <Select
                  placeholder="Chọn trạng thái"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                  showSearch={false}
                  allowClear={false}
                  notFoundContent={null}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                >
                  {/* Chỉ hiển thị các trạng thái vắng trong dropdown list */}
                  {/* Trạng thái hiện tại (nếu không phải vắng) sẽ hiển thị trong ô select nhưng không có trong dropdown */}
                  {MODAL_STATUS_OPTIONS.map((status) => (
                    <Option key={status} value={status}>
                      {status}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="date"
                label="Ngày"
                rules={[
                  { required: true, message: 'Vui lòng chọn ngày' },
                ]}
              >
                <DatePicker
                  placeholder="dd/mm/yyyy"
                  format="DD/MM/YYYY"
                  size="large"
                  className={styles.datePicker}
                  suffixIcon={<Calendar size={16} className={styles.icon} />}
                  style={{ width: '100%' }}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="employeeCode"
                label="Mã Nhân sự"
                rules={[
                  { required: true, message: 'Vui lòng nhập mã nhân sự' },
                ]}
              >
                <Input
                  placeholder="Nhập mã nhân sự"
                  size="large"
                  className={styles.input}
                  disabled
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="employeeName"
                label="Họ và tên"
                rules={[
                  { required: true, message: 'Vui lòng nhập họ và tên' },
                ]}
              >
                <Input
                  placeholder="Nhập họ và tên"
                  size="large"
                  className={styles.input}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="checkIn"
                label="Giờ vào"
              >
                <TimePicker
                  placeholder="--:--"
                  format="HH:mm"
                  size="large"
                  className={styles.timePicker}
                  suffixIcon={<Clock size={16} className={styles.icon} />}
                  style={{ width: '100%' }}
                  open={checkInOpen}
                  onOpenChange={setCheckInOpen}
                  renderExtraFooter={() => null}
                  onChange={(value) => {
                    form.setFieldsValue({ checkIn: value });
                    const checkIn = value;
                    const checkOut = form.getFieldValue('checkOut');
                    const coefficient = calculateCoefficient(checkIn, checkOut);
                    form.setFieldsValue({ coefficient });
                  }}
                  onCalendarChange={(value) => {
                    if (value && !Array.isArray(value)) {
                      form.setFieldsValue({ checkIn: value });
                      setTimeout(() => {
                        setCheckInOpen(false);
                        const checkIn = value;
                        const checkOut = form.getFieldValue('checkOut');
                        const coefficient = calculateCoefficient(checkIn, checkOut);
                        form.setFieldsValue({ coefficient });
                      }, 100);
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="checkOut"
                label="Giờ ra"
              >
                <TimePicker
                  placeholder="--:--"
                  format="HH:mm"
                  size="large"
                  className={styles.timePicker}
                  suffixIcon={<Clock size={16} className={styles.icon} />}
                  style={{ width: '100%' }}
                  open={checkOutOpen}
                  onOpenChange={setCheckOutOpen}
                  renderExtraFooter={() => null}
                  onChange={(value) => {
                    form.setFieldsValue({ checkOut: value });
                    const checkIn = form.getFieldValue('checkIn');
                    const checkOut = value;
                    const coefficient = calculateCoefficient(checkIn, checkOut);
                    form.setFieldsValue({ coefficient });
                  }}
                  onCalendarChange={(value) => {
                    if (value && !Array.isArray(value)) {
                      form.setFieldsValue({ checkOut: value });
                      setTimeout(() => {
                        setCheckOutOpen(false);
                        const checkIn = form.getFieldValue('checkIn');
                        const checkOut = value;
                        const coefficient = calculateCoefficient(checkIn, checkOut);
                        form.setFieldsValue({ coefficient });
                      }, 100);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="shift"
                label="Ca làm việc"
                rules={[
                  { required: true, message: 'Vui lòng chọn ca làm việc' },
                ]}
              >
                <Select
                  placeholder="Chọn ca làm việc"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                  loading={loadingShifts}
                  notFoundContent={loadingShifts ? 'Đang tải...' : 'Không có ca làm việc'}
                >
                  {workShifts.map((shift) => (
                    <Option key={shift.id} value={shift.name}>
                      {shift.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="coefficient"
                label="Hệ số"
                rules={[
                  { required: true, message: 'Vui lòng nhập hệ số' },
                ]}
              >
                <InputNumber
                  placeholder="Tự động tính"
                  size="large"
                  min={0}
                  max={2}
                  step={0.1}
                  className={styles.inputNumber}
                  style={{ width: '100%' }}
                  readOnly
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="overtimeHours"
                label="Giờ làm thêm"
                initialValue={0}
                normalize={(value) => value === null || value === undefined ? 0 : value}
              >
                <InputNumber
                  placeholder="Nhập giờ làm thêm"
                  size="large"
                  min={0}
                  className={styles.inputNumber}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="note"
                label="Ghi chú"
              >
                <TextArea
                  placeholder="Nhập ghi chú của bạn... (Nếu có)"
                  rows={4}
                  className={styles.textArea}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className={styles.modalFooter}>
            <Button
              size="large"
              onClick={handleCancel}
              className={styles.cancelButton}
            >
              Huỷ
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={loading}
              className={styles.submitButton}
            >
              Cập nhật
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};